import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Download, FileText, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Fase 14.2 · Bulk import CSV modal para partner_admin.
 * Endpoint POST /api/partner/{slug}/licenses/csv
 * Template download: GET /api/partner/{slug}/licenses/csv-template
 */
export default function BulkImportCSVModal({ tenantSlug, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    // preview 10 rows locally
    const text = await f.text();
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (!lines.length) return;
    const headers = lines[0].split(",").map((h) => h.trim());
    const rows = [];
    for (let i = 1; i < Math.min(lines.length, 11); i++) {
      const cells = lines[i].split(",");
      const row = {};
      headers.forEach((h, j) => { row[h] = (cells[j] || "").trim(); });
      rows.push(row);
    }
    setPreview(rows);
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get(`/partner/${tenantSlug}/licenses/csv-template`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dcc-import-template-${tenantSlug}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Plantilla descargada");
    } catch (e) {
      toast.error("Error al descargar plantilla");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Selecciona un archivo CSV"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/partner/${tenantSlug}/licenses/csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.created} licencias creadas`);
      if (onDone) onDone(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#111827] border border-gray-800 rounded-xl overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="bulk-import-modal">
        {/* Header */}
        <div className="p-6 border-b border-gray-800 flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-white font-display flex items-center gap-2">
              <Upload className="w-5 h-5" /> Importar alumnos desde CSV
            </h3>
            <p className="text-xs text-gray-500 mt-1 font-mono">/t/{tenantSlug}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {!result ? (
            <>
              {/* Instructions */}
              <div className="p-4 bg-brand-orange-500/10 border border-brand-orange-500/40 rounded-lg space-y-2">
                <p className="text-sm text-brand-orange-500 font-semibold">Formato esperado</p>
                <p className="text-xs text-gray-300">
                  El CSV debe tener las columnas: <code className="text-brand-orange-500 font-mono">email</code>, <code className="text-brand-orange-500 font-mono">full_name</code>, <code className="text-brand-orange-500 font-mono">program_code</code>.
                </p>
                <button onClick={downloadTemplate}
                  data-testid="bulk-import-template-download"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-brand-orange-500 text-brand-orange-500 hover:bg-brand-orange-500/10 font-semibold">
                  <Download className="w-3 h-3" /> Descargar plantilla
                </button>
              </div>

              {/* File input */}
              <div className="p-6 border-2 border-dashed border-gray-800 rounded-lg text-center">
                <input type="file" accept=".csv" onChange={onFileSelect}
                  data-testid="bulk-import-file-input"
                  className="hidden" id="bulk-csv-input" />
                <label htmlFor="bulk-csv-input"
                  className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-brand-orange-600 text-brand-orange-500 hover:bg-brand-orange-600/10 font-semibold">
                  <FileText className="w-4 h-4" /> {file ? "Cambiar archivo" : "Seleccionar archivo CSV"}
                </label>
                {file && <p className="mt-2 text-sm text-gray-300">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
              </div>

              {/* Preview */}
              {preview.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold mb-2">Preview (primeras 10 filas)</p>
                  <div className="bg-[#0B0F14] border border-gray-800 rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#111827] border-b border-gray-800">
                        <tr>
                          {Object.keys(preview[0]).map((h) => (
                            <th key={h} className="text-left px-3 py-2 text-gray-400 font-semibold uppercase tracking-widest text-[10px]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800">
                        {preview.map((r, i) => (
                          <tr key={i}>
                            {Object.values(r).map((v, j) => <td key={j} className="px-3 py-2 text-gray-300 font-mono">{v}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/40 rounded-lg">
                <p className="text-emerald-300 font-semibold text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> Importación completada
                </p>
                <p className="text-xs text-emerald-300/80 mt-1">
                  {result.created} creados · {result.failed} errores
                </p>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="p-4 bg-red-500/10 border border-red-500/40 rounded-lg">
                  <p className="text-red-300 font-semibold text-sm flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4" /> Errores por fila
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {result.errors.slice(0, 20).map((er, i) => (
                      <div key={i} className="text-xs text-red-200 font-mono">
                        {er.email}: {er.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800 flex gap-2">
          {!result ? (
            <>
              <button onClick={onClose}
                className="flex-1 py-2 rounded-md border border-gray-700 text-gray-400 hover:text-white text-sm font-semibold">Cancelar</button>
              <button onClick={submit} disabled={!file || submitting}
                data-testid="bulk-import-submit"
                className="flex-1 py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white text-sm font-semibold disabled:opacity-60">
                {submitting ? "Importando…" : "Importar"}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="flex-1 py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white text-sm font-semibold">Cerrar</button>
          )}
        </div>
      </div>
    </div>
  );
}
